<template>
  <div id="CampaignEditDomains" class="Main-Content">
    <Page
      v-if="loading"
      :title="`${$t('Editing')} ...`"
      :primaryAction="{
        label: $t('Add Domain'),
        disabled: true 
      }"
      :secondaryActions="[{
        label: $t('Notes'),
        disabled: true
      }]"
    >
      <LoadingLayout/>
      <LoadingLayout/>
      <LoadingLayout/>
    </Page>
    <Page
      v-if="!loading"
      :title="`${$t('Editing')} '${campaign.name}'`"
      :primaryAction="{
        label: $t('Add Domain'),
        onAction: () => { addNewDomain() }
      }"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => { eventHub.$emit('showCampaignNotes', campaign.id) }
      }]"
    >
      <campaign-edit-header :campaign="campaign"></campaign-edit-header>
      <Layout>
        <LayoutSection>
          <Card 
            :title="$t('Domains linked to this campaign')"
          >
            <DataTable
              :columns="[
                {
                  key: 'domain',
                  label: $t('Domain')
                }
              ]"
              :row-actions="row => (getAction(row))"
              :rows="filteredSitesLinked"
              :emptyText="$t('No domains')"
            />
          </Card>
          <Card 
            :title="$t('Domains not linked')"
          >
            <Filters
              :query.sync="filter"
            />
            <DataTable
              :columns="[
                {
                  key: 'domain',
                  label: $t('Domain')
                }
              ]"
              :row-actions="row => (getAction(row))"
              :rows="filteredSitesNonLinked"
              :emptyText="$t('No domains')"
            />
          </Card>
          <Card 
            :title="$t('Domains linked to other campaigns')"
          >
            <DataTable
              :columns="[
                {
                  key: 'domain',
                  label: $t('Domain')
                }
              ]"
              :row-actions="row => (getAction(row))"
              :rows="filteredSitesOther"
              :emptyText="$t('No domains')"
            />
          </Card>
        </LayoutSection>
      </Layout>

    </Page>
  </div>
</template>

<script>

import isValidDomain from 'is-valid-domain'
import CampaignEditHeader from '@/components/CampaignEditHeader'

export default {
  name: 'CampaignEditDomains',
  data () {
    return {
      loading: true,
      saving: false,
      campaign: null,
      filter: '',
      sites: [],
      campaigns: []
    }
  },
  mounted() {
    NProgress.start()
    Promise.all([this.api.getCampaign(this.$route.params.id), this.api.getCampaignNames(), this.api.getDomains()])
      .then((data) => {
        let [ campaign, campaigns, sites ] = data
        this.campaign = campaign
        this.sites = sites.sort((a, b) => {
          if (a.domain > b.domain) return 1
          if (a.domain < b.domain) return -1
          return 0
        })
        this.campaigns = campaigns
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    isCreating() {
      return this.campaign.creationStep != -1 // STEP_COMPLETE
    },
    filteredSites() {
      var filter = this.filter.trim();
      if (!filter.length) {
        return this.sites;
      } 
      return this.sites.filter((o) => {
        return (
          o.id === filter ||
          o.domain.match(new RegExp(filter))
        );
      });
    },
    filteredSitesLinked() {
      return this.sites.filter(o => o.campaignId === this.campaign.id)
    },
    filteredSitesOther() {
      return this.sites.filter(o => o.campaignId && o.campaignId !== this.campaign.id)
    },
    filteredSitesNonLinked() {
      return this.filteredSites.filter(o => !o.campaignId)
    }
  },
  methods: {
    getAction(row) {
      if (!row.campaignId) {
        return [{
          label: this.$t('Link to this Campaign'),
          onAction: (site) => this.linkToCampaign(site),
          disabled: this.saving
        }]
      } else if (this.campaign.id !== row.campaignId) {
        return [{
          label: this.$t('Linked to:') + ` '${this.getCampaignName(row.campaignId)}'`,
          onAction: (site) => (this.$router.push('/campaigns/' + site.campaignId +'/details/')),
          disabled: this.saving
        }]
      } else {
        return [{
          label: this.$t('Unlink Campaign From Domain'),
          onAction: (site) => this.unlinkCampaign(site),
          disabled: this.saving
        }]
      }
    },
    getCampaignName(campaignId) {
      let camp = this.campaigns.find(c => c.id === campaignId)
      return camp && camp.name
    },
    unlinkCampaign(site) {
      NProgress.start()
      this.saving = true
      this.api.removeSiteFromCampaign(site.domain)
        .then(() => {
          site.campaignId = null
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })     
    },
    linkToCampaign(site){
      NProgress.start()
      this.saving = true
      this.api.addSiteToCampaign(this.campaign.id, this.campaign.key, site.domain)
        .then(() => {
          site.campaignId = this.campaign.id
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    async addNewDomain() {
      const newDomain = await this.$ui.prompt({
        title: this.$t('Add New Domain'),
        content: this.$t('Enter a new domain'),
        primaryActionLabel: this.$t('Add'),
        inputPlaceholder: this.$t('Domain name'),
        inputValidator: (val) => {
          const valid = val && isValidDomain(val.trim(), { subdomain: false, wildcard: false })
          if (!valid) {
            return this.$t('Invalid domain')
          }
          let alreadyExists = false
          for (const site of this.sites) {
            if (site.domain === val) {
              alreadyExists = true
              break
            }
          }
          if (alreadyExists) {
            return this.$t('Domain already exists')
          }
          return true
        }
      })
      if (!newDomain || !newDomain.trim()) {
        return
      }
      NProgress.start()
      this.saving = true
      this.api.addAccountSite(newDomain.trim())
        .then((newDomainId) => {
          this.sites.push({
            domain: newDomain,
            id: newDomainId,
            campaignId: null
          })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    }
  },
  components: {
    CampaignEditHeader
  }
}

</script>

<style lang="scss">
#CampaignEditDomains {
  .section-header {
    padding-bottom: 46px !important;
    .section-header-actions {
      display: block;
      float: right;
    }
  }
  .filter-input {
    display: inline-block;
    margin-left: 15px;
  }
  .btn-light {
    border-color: #CCCCCC;
    background-color: #ffffff;
    margin: 2px;
    &:hover {
      background-color: #d4d4d4;
      border-color: #8c8c8c;
    }
  }
  .fa-plus-circle {
    color: #5CB85C;
  }
  .fa-link {
    color: #5CB85C;
  }
  .fa-unlink {
    color: #FD7C71;
  }
  .actions {
    .btn-success {
      background-color: #337AB7;
      border-color: #337AB7;
    }
  }
}
</style>
